import { useState } from 'react';
import {
  Briefcase,
  Search,
  Eye,
  Mail,
  Phone,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  MessageSquare,
  Building,
  DollarSign,
  ChevronRight,
  X,
} from 'lucide-react';
import { cn } from '@/lib/cn';

interface ProjectRequest {
  id: string;
  company: string;
  contactName: string;
  email: string;
  phone: string;
  projectType: string;
  budget: string;
  timeline: string;
  description: string;
  status: 'new' | 'reviewing' | 'quoted' | 'accepted' | 'rejected';
  createdAt: Date;
  notes?: string;
}

// Demo data
const initialProjects: ProjectRequest[] = [
  {
    id: '1',
    company: 'ABC Teknoloji',
    contactName: 'Ahmet Yilmaz',
    email: 'ahmet@abctech.com',
    phone: '+90 532 123 4567',
    projectType: 'Web Uygulamasi',
    budget: '50.000 - 100.000 TL',
    timeline: '3-6 ay',
    description: 'E-ticaret platformu gelistirmek istiyoruz. Stok yonetimi, odeme entegrasyonu ve mobil uyumlu tasarim gerekli.',
    status: 'new',
    createdAt: new Date('2024-01-18'),
  },
  {
    id: '2',
    company: 'XYZ Holding',
    contactName: 'Mehmet Demir',
    email: 'mehmet@xyzholding.com',
    phone: '+90 533 987 6543',
    projectType: 'Mobil Uygulama',
    budget: '100.000+ TL',
    timeline: '6+ ay',
    description: 'iOS ve Android icin kurumsal mobil uygulama. Calisanlar icin ic iletisim ve gorev yonetimi modulleri.',
    status: 'reviewing',
    createdAt: new Date('2024-01-15'),
    notes: 'Toplanti planlandi: 25 Ocak',
  },
  {
    id: '3',
    company: 'Startup Ventures',
    contactName: 'Ayse Kaya',
    email: 'ayse@startup.io',
    phone: '+90 534 555 1234',
    projectType: 'AI/ML Cozumu',
    budget: '25.000 - 50.000 TL',
    timeline: '1-3 ay',
    description: 'Musteri hizmetleri icin chatbot gelistirmek istiyoruz. Turkce dogal dil isleme gerektiriyor.',
    status: 'quoted',
    createdAt: new Date('2024-01-10'),
    notes: 'Teklif gonderildi: 45.000 TL',
  },
  {
    id: '4',
    company: 'Global Trade',
    contactName: 'Ali Ozturk',
    email: 'ali@globaltrade.com',
    phone: '+90 535 444 9876',
    projectType: 'Entegrasyon',
    budget: '10.000 - 25.000 TL',
    timeline: '1-3 ay',
    description: 'Mevcut ERP sistemimizi e-ticaret platformumuza entegre etmek istiyoruz.',
    status: 'accepted',
    createdAt: new Date('2024-01-05'),
    notes: 'Proje basladi: 20 Ocak',
  },
];

const projectTypes = ['Tumu', 'Web Uygulamasi', 'Mobil Uygulama', 'AI/ML Cozumu', 'Entegrasyon', 'Danismanlik'];

export function AdminProjects() {
  const [projects, setProjects] = useState<ProjectRequest[]>(initialProjects);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('Tumu');
  const [selectedProject, setSelectedProject] = useState<ProjectRequest | null>(null);

  // Filter projects
  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.contactName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    const matchesType = typeFilter === 'Tumu' || p.projectType === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const updateStatus = (id: string, status: ProjectRequest['status']) => {
    setProjects(projects.map(p => p.id === id ? { ...p, status } : p));
    if (selectedProject?.id === id) {
      setSelectedProject({ ...selectedProject, status });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-blue-500/10 text-blue-500 text-xs"><AlertCircle size={12} /> Yeni</span>;
      case 'reviewing':
        return <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-500/10 text-yellow-500 text-xs"><Clock size={12} /> Inceleniyor</span>;
      case 'quoted':
        return <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-purple-500/10 text-purple-500 text-xs"><DollarSign size={12} /> Teklif Verildi</span>;
      case 'accepted':
        return <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/10 text-green-500 text-xs"><CheckCircle size={12} /> Kabul Edildi</span>;
      case 'rejected':
        return <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/10 text-red-500 text-xs"><XCircle size={12} /> Reddedildi</span>;
      default:
        return null;
    }
  };

  const stats = {
    total: projects.length,
    new: projects.filter(p => p.status === 'new').length,
    reviewing: projects.filter(p => p.status === 'reviewing').length,
    accepted: projects.filter(p => p.status === 'accepted').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Proje Talepleri</h1>
        <p className="text-text-secondary">Gelen proje taleplerini yonetin</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-accent">{stats.total}</p>
          <p className="text-sm text-text-secondary">Toplam Talep</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-blue-500">{stats.new}</p>
          <p className="text-sm text-text-secondary">Yeni</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-yellow-500">{stats.reviewing}</p>
          <p className="text-sm text-text-secondary">Inceleniyor</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-500">{stats.accepted}</p>
          <p className="text-sm text-text-secondary">Kabul Edildi</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" size={18} />
          <input
            type="text"
            placeholder="Firma, kisi veya aciklama ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
        >
          <option value="all">Tum Durumlar</option>
          <option value="new">Yeni</option>
          <option value="reviewing">Inceleniyor</option>
          <option value="quoted">Teklif Verildi</option>
          <option value="accepted">Kabul Edildi</option>
          <option value="rejected">Reddedildi</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
        >
          {projectTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      {/* Projects List */}
      <div className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
        <div className="divide-y divide-border-default">
          {filteredProjects.map(project => (
            <div
              key={project.id}
              onClick={() => setSelectedProject(project)}
              className={cn(
                'p-4 cursor-pointer transition-colors',
                selectedProject?.id === project.id ? 'bg-accent/5' : 'hover:bg-bg-elevated/50'
              )}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Building size={16} className="text-text-tertiary" />
                    <h3 className="font-medium text-text-primary">{project.company}</h3>
                    {getStatusBadge(project.status)}
                  </div>
                  <p className="text-sm text-text-secondary mb-2">{project.contactName}</p>
                  <p className="text-sm text-text-tertiary line-clamp-2">{project.description}</p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-text-tertiary">
                    <span className="flex items-center gap-1">
                      <Briefcase size={12} /> {project.projectType}
                    </span>
                    <span className="flex items-center gap-1">
                      <DollarSign size={12} /> {project.budget}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar size={12} /> {project.createdAt.toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                </div>
                <ChevronRight size={20} className="text-text-tertiary" />
              </div>
            </div>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <Briefcase className="mx-auto mb-4 text-text-tertiary" size={48} />
            <p className="text-text-tertiary">Proje talebi bulunamadi</p>
          </div>
        )}
      </div>

      {/* Project Detail Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-bg-card border border-border-default rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 flex items-center justify-between p-4 bg-bg-card border-b border-border-default">
              <h2 className="text-lg font-semibold text-text-primary">Proje Detayi</h2>
              <button onClick={() => setSelectedProject(null)} className="p-1 rounded hover:bg-bg-elevated">
                <X size={20} className="text-text-tertiary" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Company & Status */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-bold text-text-primary">{selectedProject.company}</h3>
                  <p className="text-text-secondary">{selectedProject.contactName}</p>
                </div>
                {getStatusBadge(selectedProject.status)}
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <a href={`mailto:${selectedProject.email}`} className="flex items-center gap-2 p-3 rounded-lg bg-bg-elevated hover:bg-accent/10 transition-colors">
                  <Mail size={18} className="text-accent" />
                  <span className="text-sm text-text-primary">{selectedProject.email}</span>
                </a>
                <a href={`tel:${selectedProject.phone}`} className="flex items-center gap-2 p-3 rounded-lg bg-bg-elevated hover:bg-accent/10 transition-colors">
                  <Phone size={18} className="text-accent" />
                  <span className="text-sm text-text-primary">{selectedProject.phone}</span>
                </a>
              </div>

              {/* Project Details */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 rounded-lg bg-bg-elevated">
                  <p className="text-xs text-text-tertiary mb-1">Proje Tipi</p>
                  <p className="text-sm font-medium text-text-primary">{selectedProject.projectType}</p>
                </div>
                <div className="p-3 rounded-lg bg-bg-elevated">
                  <p className="text-xs text-text-tertiary mb-1">Butce</p>
                  <p className="text-sm font-medium text-text-primary">{selectedProject.budget}</p>
                </div>
                <div className="p-3 rounded-lg bg-bg-elevated">
                  <p className="text-xs text-text-tertiary mb-1">Sure</p>
                  <p className="text-sm font-medium text-text-primary">{selectedProject.timeline}</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <p className="text-xs text-text-tertiary mb-2">Proje Aciklamasi</p>
                <p className="text-text-primary">{selectedProject.description}</p>
              </div>

              {/* Notes */}
              {selectedProject.notes && (
                <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <p className="text-xs text-yellow-500 mb-1">Notlar</p>
                  <p className="text-sm text-yellow-500">{selectedProject.notes}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t border-border-default">
                <button
                  onClick={() => updateStatus(selectedProject.id, 'reviewing')}
                  className="px-4 py-2 rounded-lg bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20 text-sm"
                >
                  Incelemeye Al
                </button>
                <button
                  onClick={() => updateStatus(selectedProject.id, 'quoted')}
                  className="px-4 py-2 rounded-lg bg-purple-500/10 text-purple-500 hover:bg-purple-500/20 text-sm"
                >
                  Teklif Ver
                </button>
                <button
                  onClick={() => updateStatus(selectedProject.id, 'accepted')}
                  className="px-4 py-2 rounded-lg bg-green-500/10 text-green-500 hover:bg-green-500/20 text-sm"
                >
                  Kabul Et
                </button>
                <button
                  onClick={() => updateStatus(selectedProject.id, 'rejected')}
                  className="px-4 py-2 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 text-sm"
                >
                  Reddet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
